// src/data/deckApi.js
// Fetches the parametric card deck (cardGroups + cards) from Firestore, with
// Storage paths resolved to download URLs. Replaces the old static
// src/data/cards.js and InfiniteCanvas.js's inline duplicate arrays.
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { localizeCard, localizeGroup } from '../lib/localizeCard';

// Resolves a Storage path to a download URL, tolerating cards that were saved
// without an image (admin form only requires a label). A missing/empty path
// or a failed resolve must NOT reject the whole fetchDeck() Promise.all —
// that would take down the canvas for every user over a single bad card.
async function resolveCardImageUrl(storage, path) {
  if (!path) return '';
  try {
    return await getDownloadURL(ref(storage, path));
  } catch (e) {
    console.warn('Failed to resolve card image:', path, e);
    return '';
  }
}

// lang: active UI language. Cards/groups come back already localized
// (localizeCard/localizeGroup), Italian base fields as fallback.
export async function fetchDeck(lang = 'it') {
  const db = getFirestore();
  const storage = getStorage();

  const [groupsSnap, cardsSnap] = await Promise.all([
    getDocs(collection(db, 'cardGroups')),
    getDocs(collection(db, 'cards')),
  ]);

  const groups = groupsSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((g) => g.active)
    .sort((a, b) => a.order - b.order)
    .map((g) => localizeGroup(g, lang));

  const activeGroupIds = new Set(groups.map((g) => g.id));

  const cardsRaw = cardsSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => activeGroupIds.has(c.groupId))
    .sort((a, b) => a.order - b.order);

  // Localize BEFORE resolving, so the per-language Storage path (image_en)
  // is the one turned into a download URL. If that localized path is missing
  // or fails to resolve, fall back to the base (Italian) path so a card never
  // renders blank.
  const resolveWithFallback = async (localizedPath, basePath) => {
    const url = await resolveCardImageUrl(storage, localizedPath);
    return url || (basePath && basePath !== localizedPath ? resolveCardImageUrl(storage, basePath) : '');
  };
  const cards = await Promise.all(cardsRaw.map(async (raw) => {
    const card = localizeCard(raw, lang);
    return {
      ...card,
      image: await resolveWithFallback(card.image, raw.image),
      back: await resolveWithFallback(card.back, raw.back),
    };
  }));

  return { groups, cards };
}
